import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from "@reach/router"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
// Components
import { Toolbar } from "components/subnav"
import ImportIcon from "components/import-export/import-icon"
import ExportIcon from "components/import-export/export-icon"
import Import from "components/import-export/import"
import LineItemsTable from "components/line-items/line-items-table"
import ChartLineItemsTable from "components/line-items/chart-line-items-table"
import { WithTip } from 'components/tooltip/with-tip'
// Data
import api_match from "data/interface/api"
import importService from "data/import"
import inflationService from "data/inflation"
import lineItemsService, { LineItem } from "data/line-items"
import systemLineItemsService from "data/system-line-items"
import areaMeasuresService from "data/area-measures"
import rentrollService from 'data/rent-roll'
import propertyService from 'data/property'
import assetService from 'data/asset-list'
import { alphanum } from "components/table/lib/sort"
import { ContextRow } from './line-items-table'
import toggles from "feature-toggles"
//Styles
import { LineItems_s } from "components/line-items/line-items-styles"
import { AddIcon } from "images/icons/tools"

type PropTypes = {
  type: "COMMERCIAL" | "MULTIFAMILY" | "HOSPITALITY" | "MIXED-USE"
  litype?: "building_capital" | "expense_non_recov" | "expense" | "misc_income"
}

const LineItems: FC<RouteComponentProps & PropTypes> = (props) => {
  const [data, setData] = useState<LineItem[]>([])
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [systemLineItems, setSystemLineItems] = useState<LineItem[]>([])
  const [rentrollData, setRentrollData] = useState<object[]>([])
  const [rentrollInitSort, setRentrollInitSort] = useState<boolean>(false)
  const [inflation, setInflation] = useState<object[]>([])
  const [areaMeasures, setAreaMeasures] = useState<object[]>([])
  const [showImport, toggleShowImport] = useState<boolean>(false)
  const [property, setProperty] = useState<object|null>(null);
  const [propertyId, setPropertyId] = useState(propertyService.getStoredPropertyId())
  const [hasAssetWithChart, setHasAssetWithChart] = useState<boolean | null>(null)
  const [chartId, setChartId] = useState<number>(-1)
  const [showSelected, setShowSelected] = useState<boolean>(false)
  const [selected, setSelected] = useState<ContextRow[]>([]);
  const tableId = "line-items-table"

  useEffect(() => {
    importService.stream().subscribe(data => {
      toggleShowImport(data['showImport'])
    })

    toggleShowImport(false);

    const lineItemsSubscription = lineItemsService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          let _data = _.filter(ok.value, li => li.type !== "hosp_dept_revenue" && li.type !== "hosp_dept_expense")
          if (props.litype)
            _data = _.filter(_data, li => li.type == props.litype)
          if (props.type == 'MULTIFAMILY')
            _data = _.filter(_data, li => li.type !== 'expense_non_recov')
          setData(_data)
          setLineItems(ok.value)
        },
        err   => {
          setData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const systemLineItemsSubscription = systemLineItemsService.stream().subscribe(data => {
      api_match(
        data,
        init  => setSystemLineItems(init.value),
        ok    => {
          setSystemLineItems(ok.value)
        },
        err   => {
          setSystemLineItems(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        const property = _.find(data.value, {propertyid: data.current})
        setProperty(property)
        if(property.assetid) {
          assetService.fetchOne(property.assetid)
            .then((asset) => {
              if(_.get(asset, "chartid")) {
                setHasAssetWithChart(true)
                setChartId(_.get(asset, "chartid"))
              } else setHasAssetWithChart(false)
            })
            .catch(() => {})
        } else setHasAssetWithChart(false)
      }
      switch (data.state) {
        case 'ok':
          setPropertyId(data.current)
          break
      }
    })

    const inflationSubscription = inflationService.stream().subscribe(data => {
      api_match(
        data,
        init  => setInflation(init.value),
        ok    => {
          setInflation(ok.value)
        },
        err   => {
          setInflation(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const areaMeasuresSubscription = areaMeasuresService.stream().subscribe(data => {
      api_match(
        data,
        init  => setAreaMeasures(init.value),
        ok    => {
          setAreaMeasures(ok.value)
        },
        err   => {
          setAreaMeasures(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const rentrollSubscription = rentrollService.stream().subscribe(data => {
      api_match(
        data,
        init  => setRentrollData(init.value),
        ok    => {
          if(!rentrollInitSort && ok.value.length) { // sort only on first load
            setRentrollData([...ok.value].sort((a, b) => alphanum(_.get(a, "suite"), _.get(b, "suite"))))
            setRentrollInitSort(true)
          } else {
            setRentrollData(ok.value)
          }
        },
        err   => {
          setRentrollData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    return function cleanup() {
      lineItemsSubscription.unsubscribe()
      systemLineItemsSubscription.unsubscribe()
      propertySubscription.unsubscribe()
      inflationSubscription.unsubscribe()
      areaMeasuresSubscription.unsubscribe()
      rentrollSubscription.unsubscribe()
    }
  }, [props.litype])

  useEffect(() => {
    if (_.isNil(propertyId)) return

    lineItemsService.fetchLineItems(propertyId)
    systemLineItemsService.fetchSystemLineItems(propertyId)
    propertyService.fetchProperty(propertyId)
    inflationService.fetchInflation(propertyId)
    areaMeasuresService.fetchAreaMeasures(propertyId)
    rentrollService.fetchRentRoll(propertyId, false)

  }, [propertyId])

  const Add = () => {
    let defaults = {}
    if (property) _.assign(defaults, property['defaultslineitem'])
    if (props.litype) _.assign(defaults, {type: props.litype})
    return (
      <a onClick={() => lineItemsService.create(propertyService.getStoredPropertyId(), defaults, props.type)}>
        <Isvg src={AddIcon} width="20px" />
      </a>
    )
  }

  const [deleteFlag, setDeleteFlag] = useState(0)
  const Delete = () => {
    return (
      <WithTip position="bottom">
        <span>Delete Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput')  ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setDeleteFlag(deleteFlag+1)
        }}>
          <span className="p-menuitem-icon pi pi-fw pi-trash"></span>
        </a>
      </WithTip>
    )
  }

  const [pasteFlag, setPasteFlag] = useState(0)
  const Paste = () => {
    return (
      <WithTip position="bottom">
        <span>Paste to Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput') ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setPasteFlag(pasteFlag+1)
          }
        }>
          <span className="p-menuitem-icon pi pi-fw pi-clone"></span>
        </a>
      </WithTip>
    )
  }

  const ShowSelected = () => (
    <WithTip position="bottom">
      <span>Show {showSelected ? 'All' : 'Selected'} Rows</span>
      <a className={selected.length ? '' : 'disabled'}
        onClick={() => {
          if (!selected.length) return
          setShowSelected(!showSelected)
        }}>
        <span className={showSelected ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{height: '18px', width: '18px'}}></span>
      </a>
    </WithTip>
  )

  useEffect(() => {
    setSelected(_.intersectionBy(data, selected, 'lineitemid'))
  }, [data])

  useEffect(() => {
    if (showSelected && _.isEmpty(selected)) setShowSelected(false)
  }, [selected])

  const _data = showSelected ? selected : data

  return (
    <LineItems_s>
      <Toolbar>
        <Add />
        {toggles.multiselect() && <>
          <Paste />
          <Delete />
          <ShowSelected />
        </>}
        <ImportIcon />
        <ExportIcon template="lineitem" />
      </Toolbar>
      { showImport && <Import /> }
      { data && property && !_.isNil(hasAssetWithChart) && property['defaultslineitem'] && inflation && lineItems && areaMeasures && systemLineItems && rentrollData && !showImport &&
        (hasAssetWithChart ?
        <ChartLineItemsTable tableId={tableId} data={_data} type={props.type}
          lineItemsData={lineItems}
          systemLineItemsData={systemLineItems}
          inflationData={inflation}
          areaMeasuresData={areaMeasures}
          rentrollData={rentrollData}
          chartId={chartId}
          defaults={property['defaultslineitem']}
          selectedState={[selected, setSelected]} flags={{deleteFlag, pasteFlag}} />
        :
        <LineItemsTable tableId={tableId} data={_data} type={props.type} litype={props.litype}
          hasAsset={_.get(property, "assetid") ? true : false}
          lineItemsData={lineItems}
          systemLineItemsData={systemLineItems}
          inflationData={inflation}
          areaMeasuresData={areaMeasures}
          rentrollData={rentrollData}
          defaults={property['defaultslineitem']}
          selectedState={[selected, setSelected]} flags={{deleteFlag, pasteFlag}} />)
      }
    </LineItems_s>
  )
}

export default LineItems
